import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/api';
import type { RecurringTemplateWithClient } from '../types/recurring-reminders';

/**
 * Fetch recurring reminder templates for a specific client
 */
async function fetchRecurringTemplates(
  clientId: string,
  accessToken: string | null | undefined,
) {
  return apiRequest<RecurringTemplateWithClient[]>(
    `/api/clients/${clientId}/reminders/recurring`,
    { method: 'GET' },
    accessToken,
  );
}

/**
 * Fetch a single recurring reminder template by ID
 */
async function fetchRecurringTemplate(
  clientId: string,
  templateId: string,
  accessToken: string | null | undefined,
) {
  return apiRequest<RecurringTemplateWithClient>(
    `/api/clients/${clientId}/reminders/recurring/${templateId}`,
    { method: 'GET' },
    accessToken,
  );
}

/**
 * Hook to fetch all recurring templates for a client
 */
export function useRecurringTemplates(clientId: string) {
  const { session } = useAuth();

  return useQuery<RecurringTemplateWithClient[]>({
    queryKey: ['recurring-templates', clientId],
    enabled: Boolean(clientId && session),
    queryFn: () => fetchRecurringTemplates(clientId, session?.access_token),
  });
}

/**
 * Hook to fetch a single recurring template
 */
export function useRecurringTemplate(clientId: string, templateId: string) {
  const { session } = useAuth();

  return useQuery<RecurringTemplateWithClient>({
    queryKey: ['recurring-template', clientId, templateId],
    enabled: Boolean(clientId && templateId && session),
    queryFn: () => fetchRecurringTemplate(clientId, templateId, session?.access_token),
  });
}

/**
 * Hook to invalidate recurring templates cache
 */
export function useInvalidateRecurringTemplates(clientId: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: ['recurring-templates', clientId],
    });
    // Also invalidate reminders since new instances may have been generated
    queryClient.invalidateQueries({
      queryKey: ['reminders', clientId],
    });
  };
}
